import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { apiRequest } from '@/src/services';
import { getZeroOption, getValidSelectValue, type Option, Constants } from '@/src/utils';
import {
    SimpleButton,
    SimpleSingleSelect,
    ErrorMessage,
    FilterIcon,
    ICON_COLORS,
    FilterBarContainer,
} from '@/src/ui';
import type {
    ClassMatrixReportFilterState,
    ClassRow,
    InitialOptions,
    SchoolClassOptions,
    ExamOption,
} from '../models/filterDTOs';

interface ClassMatrixFiltersProps {
    refreshTableData: (state: ClassMatrixReportFilterState) => void | Promise<void>;
    filterState: ClassMatrixReportFilterState;
    setFilterState: React.Dispatch<React.SetStateAction<ClassMatrixReportFilterState>>;
}

const AllOption = getZeroOption('All');
const LoadingOption = getZeroOption('Loading...');
const SelectDistrictOption = getZeroOption('Select District');
const SelectSchoolOption = getZeroOption('Select School');
const SelectClassOption = getZeroOption('Select Class');
const SelectGradeOption = getZeroOption('Select Grade');
const SelectExamOption = getZeroOption('Select Exam');
const NoClassOption = getZeroOption('No Classes Available');
const NoExamOption = getZeroOption('No Exams Available');

const ClassMatrixReportFilters: React.FC<ClassMatrixFiltersProps> = ({
    refreshTableData,
    filterState,
    setFilterState,
}) => {
    const [districtOptions, setDistrictOptions] = useState<Option[]>([LoadingOption]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([SelectSchoolOption]);
    const [classOptions, setClassOptions] = useState<Option[]>([SelectClassOption]);
    const [gradeLevelMasterOptions, setGradeLevelMasterOptions] = useState<Option[]>([
        LoadingOption,
    ]);
    const [examTypeMasterOptions, setExamTypeMasterOptions] = useState<Option[]>([AllOption]);
    const [allExamOptions, setAllExamOptions] = useState<ExamOption[]>([]);
    const [examOptions, setExamOptions] = useState<Option[]>([SelectExamOption]);

    const [classData, setClassData] = useState<ClassRow[]>([]);
    const [fieldsChanged, setFieldsChanged] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [filtersLoading, setFiltersLoading] = useState(true);

    const getLoadingProps = (opts: Option[], val: number, loading: boolean) =>
        loading
            ? { options: [LoadingOption], value: 0, disabled: true }
            : { options: opts, value: val };

    const requiredFields = [
        'districtId',
        'schoolId',
        'gradeLevelMasterId',
        'classId',
        'examId',
    ] as const;
    function getMissingFields(fs: any): string[] {
        return requiredFields
            .filter((key) => !fs[key] || Number(fs[key]) === 0)
            .map((key) => Constants.fieldLabels[key]);
    }

    const updateClassOptions = (data: ClassRow[], gradeLevelMasterId: number, schoolId: number) => {
        if (
            !schoolId ||
            schoolId === Number(SelectSchoolOption.id) ||
            !gradeLevelMasterId ||
            gradeLevelMasterId === Number(SelectGradeOption.id)
        ) {
            setClassOptions([SelectClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(AllOption.id) }));
            return;
        }

        const newClassOptions = data
            .filter(
                (option) =>
                    Number(option.schoolId) === Number(schoolId) &&
                    (!option.gradeLevelMasterIds?.length ||
                        option.gradeLevelMasterIds.includes(gradeLevelMasterId))
            )
            .map((option) => ({ id: option.id, name: option.name }));

        if (!newClassOptions.length) {
            setClassOptions([NoClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(NoClassOption.id) }));
        } else {
            setClassOptions([SelectClassOption, ...newClassOptions]);
            setFilterState((prev) => {
                const classExists = newClassOptions.some(
                    (opt) => Number(opt.id) === Number(prev.classId)
                );
                if (classExists) {
                    return prev;
                }
                return {
                    ...prev,
                    classId: Number(AllOption.id),
                };
            });
        }
    };

    const fetchSchoolClassOptions = async (districtId: number | string) => {
        if (!districtId || Number(districtId) === Number(AllOption.id)) {
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([AllOption]);
            setFilterState((prev) => ({
                ...prev,
                schoolId: Number(SelectSchoolOption.id),
                gradeLevelMasterId: Number(SelectGradeOption.id),
                classId: Number(AllOption.id),
            }));
            return;
        }

        try {
            const res = await apiRequest<SchoolClassOptions>(
                'GET',
                `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
            );
            const data = res.data;
            const newSchoolOptions = (data.schoolOptions ?? []).sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            setSchoolOptions([SelectSchoolOption, ...newSchoolOptions]);
            setFilterState((prev) => {
                const schoolExists = newSchoolOptions.some(
                    (opt) => Number(opt.id) === Number(prev.schoolId)
                );
                return {
                    ...prev,
                    schoolId: schoolExists ? prev.schoolId : Number(SelectSchoolOption.id),
                };
            });
            const sortedClassOptions = (data.classOptions ?? []).sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            setClassData(sortedClassOptions);
            setClassOptions([
                SelectClassOption,
                ...sortedClassOptions.map((option) => ({ id: option.id, name: option.name })),
            ]);
            setFilterState((prev) => {
                const classExists = sortedClassOptions.some(
                    (opt) => Number(opt.id) === Number(prev.classId)
                );
                return {
                    ...prev,
                    classId: classExists ? prev.classId : Number(AllOption.id),
                };
            });
        } catch {
            setErrorMsg('Failed to fetch school and class options');
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([SelectClassOption]);
        }
    };

    const maybeFetchExamOptions = async (newFilterState: ClassMatrixReportFilterState) => {
        const { gradeLevelMasterId } = newFilterState;

        // Only fetch exams if a grade is selected
        if (!gradeLevelMasterId || gradeLevelMasterId === Number(SelectGradeOption.id)) {
            setExamOptions([SelectExamOption]);
            setAllExamOptions([]);
            setFilterState((prev) => ({ ...prev, examId: Number(SelectExamOption.id) }));
            return;
        }

        try {
            const res = await apiRequest<{ examOptions: ExamOption[] }>(
                'GET',
                `utility/filterData?getExam=true&gradeLevelMasterIds=${gradeLevelMasterId}&showPublishedExamsOnly=true`
            );
            const data = res.data.examOptions || [];

            const filtered = data.filter((exam) => exam.schoolIds && exam.schoolIds.length > 0);
            const sorted = filtered.sort((a, b) => a.name.localeCompare(b.name));

            setAllExamOptions(sorted);
            filterExamsLocally(newFilterState.classId, newFilterState.examTypeMasterId, sorted);
        } catch {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(NoExamOption.id) }));
            setErrorMsg('Failed to load exam options');
        }
    };

    const filterExamsLocally = (
        classId: number,
        examTypeMasterId: number,
        allExamsData?: ExamOption[]
    ) => {
        const exams = allExamsData || allExamOptions;
        if (!exams.length) {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(NoExamOption.id) }));
            return;
        }

        let filtered = exams;
        if (classId && classId !== Number(AllOption.id)) {
            filtered = filtered.filter((e) => !e.classIds || e.classIds.includes(classId));
        }

        if (examTypeMasterId && examTypeMasterId !== Number(AllOption.id)) {
            filtered = filtered.filter((e) => e.examTypeMasterId === examTypeMasterId);
        }

        setExamOptions(filtered.length ? [SelectExamOption, ...filtered] : [NoExamOption]);
        setFilterState((prev) => ({ ...prev, examId: Number(SelectExamOption.id) }));
    };

    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true&getDistrict=true&getExamTypeMaster=true'
                );
                const data = res.data;

                setGradeLevelMasterOptions([
                    SelectGradeOption,
                    ...(data.gradeLevelMasterOptions ?? []),
                ]);
                setDistrictOptions([
                    SelectDistrictOption,
                    ...(data.districtOptions ?? []).sort((a, b) => a.name.localeCompare(b.name)),
                ]);
                setExamTypeMasterOptions([
                    AllOption,
                    ...(data.examTypeMasterOptions ?? []).sort((a, b) =>
                        a.name.localeCompare(b.name)
                    ),
                ]);

                if (
                    filterState &&
                    filterState.districtId &&
                    filterState.districtId !== Number(SelectDistrictOption.id)
                ) {
                    await fetchSchoolClassOptions(filterState.districtId);
                    setFiltersLoading(false);
                } else {
                    setFiltersLoading(false);
                }
            } catch {
                setErrorMsg('Failed to load initial filter options');
                setFiltersLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (
            filterState &&
            filterState.schoolId &&
            filterState.schoolId !== Number(SelectSchoolOption.id) &&
            filterState.gradeLevelMasterId &&
            filterState.gradeLevelMasterId !== Number(SelectGradeOption.id)
        ) {
            maybeFetchExamOptions(filterState);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState.schoolId, filterState.classId, filterState.gradeLevelMasterId]);

    useEffect(() => {
        if (
            filterState &&
            filterState.schoolId &&
            filterState.schoolId !== Number(SelectSchoolOption.id) &&
            filterState.gradeLevelMasterId &&
            filterState.gradeLevelMasterId !== Number(SelectGradeOption.id) &&
            classData.length > 0
        ) {
            updateClassOptions(classData, filterState.gradeLevelMasterId, filterState.schoolId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classData, filterState.schoolId, filterState.gradeLevelMasterId]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                justifyContent: 'center',
                mb: 2,
            }}
        >
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="district-select"
                    label="District *"
                    {...getLoadingProps(
                        districtOptions,
                        Number(filterState.districtId),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newDistrictId = Number(e.target.value);
                        const newState = {
                            ...filterState,
                            districtId: newDistrictId,
                            schoolId: Number(SelectSchoolOption.id),
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(AllOption.id),
                        };
                        setFilterState(newState);
                        fetchSchoolClassOptions(newDistrictId);
                        setExamOptions([AllOption]);
                        setAllExamOptions([]);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="school-select"
                    label="School *"
                    {...getLoadingProps(
                        schoolOptions,
                        Number(filterState.schoolId),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newSchoolId = Number(e.target.value);
                        const newState = {
                            ...filterState,
                            schoolId: newSchoolId,
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(AllOption.id),
                        };
                        setFilterState(newState);
                        updateClassOptions(classData, Number(SelectGradeOption.id), newSchoolId);
                        setExamOptions([AllOption]);
                        setAllExamOptions([]);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={filtersLoading || filterState.districtId === Number(AllOption.id)}
                />
                <SimpleSingleSelect
                    name="grade-select"
                    label="Grade Level *"
                    {...getLoadingProps(
                        gradeLevelMasterOptions,
                        Number(filterState.gradeLevelMasterId),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newGradeLevelMasterId = Number(e.target.value);
                        const newState = {
                            ...filterState,
                            gradeLevelMasterId: newGradeLevelMasterId,
                        };
                        setFilterState(newState);
                        updateClassOptions(classData, newGradeLevelMasterId, newState.schoolId);
                        maybeFetchExamOptions(newState);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading || filterState.schoolId === Number(SelectSchoolOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="class-select"
                    label="Class *"
                    {...getLoadingProps(
                        classOptions,
                        Number(
                            getValidSelectValue(
                                filterState.classId,
                                classOptions,
                                classOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newClassId = Number(e.target.value);
                        const newState = { ...filterState, classId: newClassId };
                        setFilterState(newState);
                        filterExamsLocally(newClassId, filterState.examTypeMasterId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        filterState.schoolId === Number(SelectSchoolOption.id) ||
                        filterState.gradeLevelMasterId === Number(SelectGradeOption.id)
                    }
                />
            </FilterBarContainer>
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="exam-type-select"
                    label="Type"
                    value={filterState.examTypeMasterId}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newExamTypeMasterId = Number(e.target.value);
                        const newState = { ...filterState, examTypeMasterId: newExamTypeMasterId };
                        setFilterState(newState);
                        filterExamsLocally(filterState.classId, newExamTypeMasterId);
                    }}
                    options={examTypeMasterOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="exam-select"
                    label="Assessment *"
                    value={filterState.examId}
                    onChange={(e) =>
                        setFilterState((prev) => ({ ...prev, examId: Number(e.target.value) }))
                    }
                    options={examOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleButton
                    onClick={() => {
                        const missing = getMissingFields(filterState);
                        if (missing.length) {
                            setErrorMsg(`Please select: ${missing.join(', ')} before filtering.`);
                            return;
                        }
                        setErrorMsg(null);
                        setFieldsChanged(false);
                        refreshTableData(filterState);
                    }}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ padding: '8px 25px' }}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: '16px', height: '16px', marginRight: '8px' }}
                    />{' '}
                    Filter
                </SimpleButton>
            </FilterBarContainer>

            <ErrorMessage error={errorMsg} />
        </Box>
    );
};

export default ClassMatrixReportFilters;
